import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries } from '@/lib/queryHelpers'

// Types
export interface TimesheetEntry {
  id: number
  user_id: number
  work_date: string
  clock_in: string | null
  clock_out: string | null
  hours: number | null
  notes: string | null
  image_urls: string[]
  // EP-154: a shift clocked from the dashboard. `open` while clocked in; the
  // selfie and the phone's location from each tap.
  open?: boolean
  /** The two taps as instants (ISO 8601, UTC), for elapsed time on the card. */
  clock_in_at?: string | null
  clock_out_at?: string | null
  clock_in_photo_url?: string | null
  clock_out_photo_url?: string | null
  clock_in_location?: ClockLocation | null
  clock_out_location?: ClockLocation | null
  created_at: string
  updated_at: string
}

export interface ClockLocation {
  lat: number
  lng: number
  accuracy?: number | null
  captured_at?: string | null
}

export interface TimesheetLock {
  id: number
  user_id: number
  week_start: string
  locked_by: { id: number; name: string }
  created_at: string
}

export interface TimesheetWeekResponse {
  entries: TimesheetEntry[]
  lock: TimesheetLock | null
  user: { id: number; name: string; email: string; role: string }
}

export interface TimesheetUserRow {
  user: { id: number; name: string; email: string; role: string }
  entries: TimesheetEntry[]
  lock: TimesheetLock | null
}

export interface TimesheetAllUsersResponse {
  users: TimesheetUserRow[]
}

// Query keys
export const timesheetKeys = {
  all: ['timesheet'] as const,
  weeks: () => [...timesheetKeys.all, 'week'] as const,
  week: (week: string, userId?: number | 'all') =>
    [...timesheetKeys.weeks(), week, userId ?? 'self'] as const,
}

// Hooks
export function useTimesheetEntries(week: string, userId?: number) {
  return useQuery({
    queryKey: timesheetKeys.week(week, userId),
    queryFn: async () => {
      const params: Record<string, unknown> = { week }
      if (userId) params.user_id = userId
      const { data } = await api.get<TimesheetWeekResponse>('/api/timesheet_entries', { params })
      return data
    },
    enabled: !!week,
  })
}

export function useTimesheetAllUsers(week: string, enabled: boolean) {
  return useQuery({
    queryKey: timesheetKeys.week(week, 'all'),
    queryFn: async () => {
      const { data } = await api.get<TimesheetAllUsersResponse>('/api/timesheet_entries', {
        params: { week, user_id: 'all' },
      })
      return data
    },
    enabled: !!week && enabled,
  })
}

export interface CreateTimesheetEntryInput {
  work_date: string
  clock_in?: string | null
  clock_out?: string | null
  hours?: number | null
  notes?: string | null
  image_urls?: string[]
  // T141-011 — who the shift is attributed to. Absent defaults to
  // current_user server-side (unchanged); admin/owner may target any
  // same-account user (authorized via TimesheetBlockPolicy, not a role
  // check), staff/accountant are refused targeting anyone but themselves.
  // Sent as a TOP-LEVEL sibling of `timesheet_entry` below, not nested
  // inside it — the backend reads `params[:user_id]` directly, the same
  // shape GET /api/timesheet_entries already uses for its own `?user_id=`,
  // NOT the nested `timesheet_lock: { user_id }` shape
  // POST /api/timesheet_locks uses (see useLockWeek below); the two
  // endpoints disagree on this and each must be matched exactly.
  user_id?: number
}

export function useCreateTimesheetEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateTimesheetEntryInput) => {
      const { user_id, ...entry } = input
      const { data } = await api.post<{ data: TimesheetEntry }>('/api/timesheet_entries', {
        timesheet_entry: entry,
        ...(user_id !== undefined ? { user_id } : {}),
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, timesheetKeys.weeks())
    },
  })
}

export interface UpdateTimesheetEntryInput {
  id: number
  work_date?: string
  clock_in?: string | null
  clock_out?: string | null
  hours?: number | null
  notes?: string | null
  image_urls?: string[]
}

export function useUpdateTimesheetEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateTimesheetEntryInput) => {
      const { data } = await api.patch<{ data: TimesheetEntry }>(`/api/timesheet_entries/${id}`, {
        timesheet_entry: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, timesheetKeys.weeks())
    },
  })
}

export function useDeleteTimesheetEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/timesheet_entries/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, timesheetKeys.weeks())
    },
  })
}

export interface LockWeekInput {
  week_start: string
  user_id?: number
}

export function useLockWeek() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: LockWeekInput) => {
      const { data } = await api.post<{ data: TimesheetLock }>('/api/timesheet_locks', {
        timesheet_lock: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, timesheetKeys.weeks())
    },
  })
}

export function useUnlockWeek() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (lockId: number) => {
      await api.delete(`/api/timesheet_locks/${lockId}`)
      return lockId
    },
    onSuccess: () => {
      removeListQueries(queryClient, timesheetKeys.weeks())
    },
  })
}
